import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FilamentComparePanel from '../../../components/feature/FilamentComparePanel';
import { FilamentStressStrainChart } from '../../../components/feature/FilamentStressStrainChart';
import { useFilamentText } from '../../../hooks/useFilamentText';
import { useSEO } from '../../../hooks/useSEO';
import BendingStrengthCard from '../../../components/feature/BendingStrengthCard';

const PLAPlusPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const TEXT = useFilamentText();

  useSEO({
    title: 'PLA+',
    description: isEnglish 
      ? 'Enhanced impact resistance over standard PLA. Improved mechanical strength while maintaining ease of use. Detailed explanation of recommended settings and features.'
      : '通常PLAより高い耐衝撃性。扱いやすさを維持しつつ機械的強度が向上。推奨設定、特徴を詳しく解説。',
    keywords: '3Dプリンター,PLA+,フィラメント,高強度,耐衝撃性',
    canonicalPath: isEnglish ? '/en/filament/pla-plus' : '/filament/pla-plus'
  });

  const breadcrumbItems = isEnglish ? [
    { label: 'Home', path: '/en' },
    { label: 'Filament Data', path: '/filament' },
    { label: 'PLA', path: '/en/filament/pla' },
    { label: 'PLA+', path: '/en/filament/pla-plus' }
  ] : [
    { label: 'ホーム', path: '/' },
    { label: 'フィラメントデータ', path: '/filament' },
    { label: 'PLA', path: '/filament/pla' },
    { label: 'PLA+', path: '/filament/pla-plus' }
  ];

  const filamentId = 'pla-plus';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          {/* メインコンテンツ */}
          <div className="space-y-8 mt-8">
            {/* タイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                PLA+
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 比較指標 */}
            <FilamentComparePanel slug="pla-plus" />

            {/* 特徴 */}
            <div className="metal-panel p-8 border-l-4 border-accent">
              <h2 className="text-3xl font-semibold text-white font-['Exo'] mb-6 accent-line pb-3">
                {TEXT.features}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="text-metal-light space-y-3 text-lg">
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Higher impact resistance than standard PLA' : '通常PLAより高い耐衝撃性'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Improved layer adhesion' : '改良された層間接着'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Maintains PLA ease of use' : 'PLAの扱いやすさを維持'}</span>
                  </li>
                </ul>
                <ul className="text-metal-light space-y-3 text-lg">
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Enhanced mechanical strength' : '機械的強度の向上'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Slightly higher heat resistance' : 'わずかに高い耐熱性'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Excellent surface finish' : '優れた表面仕上がり'}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 基本情報 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.classification}</span>
                      <span className="difficulty-beginner px-3 py-1 rounded text-sm font-semibold">{TEXT.beginner}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.baseResin}</span>
                      <span className="text-white">{isEnglish ? 'Modified Polylactic Acid' : '改良ポリ乳酸'}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.meltingPoint}</span>
                      <span className="text-white">160-170°C</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.density}</span>
                      <span className="text-white">1.25 g/cm³</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.shrinkageRate}</span>
                      <span className="text-white">0.3-0.4%</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.heatResistance}</span>
                      <span className="text-white">60-70°C</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 推奨設定 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.recommendedSettings}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#3a3d42] text-white">
                      <th className="px-4 py-3 text-left">{TEXT.item}</th>
                      <th className="px-4 py-3 text-left">{TEXT.recommendedValue}</th>
                      <th className="px-4 py-3 text-left">{TEXT.notes}</th>
                    </tr>
                  </thead>
                  <tbody className="text-metal-light">
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleTemp}</td>
                      <td className="px-4 py-3 text-white">200-230°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Higher than standard PLA' : '通常PLAより高温'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.bedTemp}</td>
                      <td className="px-4 py-3 text-white">50-70°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Improved adhesion' : '密着性向上'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.fanSetting}</td>
                      <td className="px-4 py-3 text-white">80-100%</td>
                      <td className="px-4 py-3">{isEnglish ? 'From layer 2' : '2層目から'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.printSpeed}</td>
                      <td className="px-4 py-3 text-white">40-70mm/s</td>
                      <td className="px-4 py-3">{isEnglish ? 'Faster than standard PLA' : '通常PLAより高速可'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleDiameter}</td>
                      <td className="px-4 py-3 text-white">0.4mm</td>
                      <td className="px-4 py-3">{isEnglish ? 'Standard setting' : '標準設定'}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">{TEXT.caution}</td>
                      <td className="px-4 py-3 text-white" colSpan={2}>{isEnglish ? 'Moisture sensitive, temperature adjustment important' : '湿気保管注意、温度調整重要'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 応力-ひずみグラフ（CSV自動探索） */}
            {/* 曲げ強度 */}
            <BendingStrengthCard slug={filamentId} isEnglish={isEnglish} />

            <FilamentStressStrainChart 
              filamentId={filamentId}
              filamentName="PLA+"
            />

            {/* 派生・バリエーション */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.derivatives}
              </h2>
              <div className="space-y-4 text-metal-light">
                <div className="border-l-2 border-accent pl-4">
                  <span className="text-white font-semibold">PLA+ Pro</span>
                  <p className="mt-1">{isEnglish ? 'Further enhanced PLA+. Higher impact and heat resistance.' : 'さらに強化されたPLA+。より高い耐衝撃性と耐熱性を実現。'}</p>
                </div>
                <div className="border-l-2 border-accent pl-4">
                  <span className="text-white font-semibold">PLA+ Tough</span>
                  <p className="mt-1">{isEnglish ? 'Toughness-focused formulation. Bend-resistant, suitable for mechanical parts.' : '靭性重視の配合。曲げに強く、機械部品にも使用可能。'}</p>
                </div>
              </div>
            </div>

            {/* 使用例 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.useCases}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Suitable Applications' : '向いている造形'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    <li>• {isEnglish ? 'Functional prototypes' : '機能的プロトタイプ'}</li>
                    <li>• {isEnglish ? 'Tools & jigs' : '工具・治具'}</li>
                    <li>• {isEnglish ? 'Durable parts' : '耐久性重視の部品'}</li>
                    <li>• {isEnglish ? 'Thin-walled structures' : '薄肉構造物'}</li>
                    <li>• {isEnglish ? 'Moving parts' : '可動部品'}</li>
                    <li>• {isEnglish ? 'Cases & enclosures' : 'ケース・筐体'}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Pros & Cons' : '長所・短所'}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-green-400">{isEnglish ? 'Pros:' : '長所：'}</span>
                      <span className="text-metal-light"> {isEnglish ? 'High strength, easy to use, good finish' : '高強度、扱いやすい、良好な仕上がり'}</span>
                    </div>
                    <div>
                      <span className="text-red-400">{isEnglish ? 'Cons:' : '短所：'}</span>
                      <span className="text-metal-light"> {isEnglish ? 'More expensive than standard PLA, requires temperature adjustment' : '通常PLAより高価、温度調整必要'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.precautions}
              </h2>
              <div className="text-metal-light space-y-2">
                <p>• {isEnglish ? 'Higher temperature settings required than standard PLA.' : '通常PLAより高い温度設定が必要です。'}</p>
                <p>• {isEnglish ? 'Properties vary by brand, test prints recommended.' : 'ブランドにより特性が異なるため、テスト印刷を推奨します。'}</p>
                <p>• {isEnglish ? 'Moisture sensitive, store in sealed container.' : '湿気に敏感なため、密閉保管してください。'}</p>
                <p>• {isEnglish ? 'Strong layer adhesion, be careful when removing supports.' : '層間接着が強いため、サポート除去に注意が必要です。'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PLAPlusPage;
